"use client"

import { useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import HeaderLogo from "../assets/Headerlogo.jpeg"; 

const DropdownArrow = ({ className }) => (
  <svg
    className={className || "dropdown-arrow"}
    width="12"
    height="8"
    viewBox="0 0 12 8"
    fill="none"
  >
    <path
      d="M1 1.5L6 6.5L11 1.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const MobileDropdownArrow = ({ className }) => (
  <svg
    className={className || "dropdown-arrow"}
    width="12"
    height="8"
    viewBox="0 0 12 8"
    fill="none"
  >
    <path
      d="M1 1.5L6 6.5L11 1.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function Header() {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
    if (isMobileMenuOpen) {
      setActiveDropdown(null)
    }
  }

  const toggleMobileDropdown = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown)
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
    setActiveDropdown(null)
  }

  return (
    <>
      {/* ✨ NEW: CSS styles are now included directly in this file */}
      <style>
        {`
          /* ===== Default Styles (For Desktop) ===== */
          
          .logo img {
            height: 50px;
            width: auto;
            display: block; /* Prevents extra space below the image */
          }
          
          .header-content {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          .right-actions {
            display: flex;
            align-items: center;
            gap: 1rem; /* Adds space between the button and hamburger icon */
          }
          
          
          /* ===== Mobile & Tablet Header Adjustments ===== */
          
          /* This @media query applies styles ONLY to screens 1099px wide or smaller */
          @media (max-width: 1099px) {
          
            /* Make the header bar shorter (more compact) */
            .header .container {
              padding-top: 8px;
              padding-bottom: 8px;
            }
          
            /* Reduce the logo size on mobile and tablet */
            .logo img {
              height: 38px; 
            }
          
            /* Make the 'Log in/Sign Up' button smaller and more compact */
            .right-actions .contact-btn {
              padding: 6px 14px;
              font-size: 0.8rem;
            }
            
            /* Reduce gap between button and menu on smaller screens */
            .right-actions {
              gap: 0.5rem;
            }
          }
          
          /* Extra small screens */
          @media (max-width: 480px) {
            .logo img {
              height: 32px;
            }
            
            .right-actions .contact-btn {
              padding: 5px 10px;
              font-size: 0.75rem;
            }
          }
        `}
      </style>

      <header className="header">
        <div className="container">
          <div className="header-content">
            {/* Logo */}
            <div className="logo">
              <Link to="/">
                {/* MODIFICATION: Removed inline styles to control via CSS */}
                <img 
                  src={HeaderLogo} 
                  alt="WayForSky Logo" 
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="desktop-nav">
              <Link
                to="/"
                className={`nav-link focus-ring ${location.pathname === "/" ? "active" : ""}`}
              >
                Home
              </Link>

              <div className="dropdown-container">
                <Link
                  to="/courses"
                  className={`nav-link focus-ring dropdown-trigger ${
                    location.pathname.startsWith("/courses") || location.pathname.startsWith("/zero-to-hero") || location.pathname.startsWith("/commercial-pilot-license") || ["/dgca-classes", "/elp-classes", "/nios-classes"].includes(location.pathname) ? "active" : ""
                  }`}
                >
                  Courses
                  <DropdownArrow />
                </Link>
                <div className="dropdown-menu">
                  <Link to="/zero-to-hero" className="dropdown-item">
                    Zero to Hero Airline Ready Program - EASA
                  </Link>
                  <Link to="/commercial-pilot-license" className="dropdown-item">
                    Commercial Pilot License with MEIR - SACAA
                  </Link>
                  <Link to="/private-pilot-license" className="dropdown-item">
                    Private Pilot License
                  </Link>
                  <Link to="/flight-instructor-rating" className="dropdown-item">
                    Flight Instructor Rating
                  </Link>
                  <Link to="/type-rating" className="dropdown-item">
                    Type Rating A320 and B737 Cabin Crew
                  </Link>
                  <Link to="/cabin-crew" className="dropdown-item">
                    Cabin Crew
                  </Link>
                  <Link to="/dgca-classes" className="dropdown-item">
                    DGCA ground Classes
                  </Link>
                  <Link to="/elp-classes" className="dropdown-item">
                    English Language Proficiency (ELP) Training
                  </Link>
                  <Link to="/nios-classes" className="dropdown-item">
                    NIOS Prep Classes
                  </Link>
                </div>
              </div>

              <Link
                to="/schools"
                className={`nav-link focus-ring ${location.pathname === "/schools" ? "active" : ""}`}
              >
                Schools
              </Link>

              <div className="dropdown-container">
                <Link
                  to="/fleet"
                  className={`nav-link focus-ring dropdown-trigger ${
                    location.pathname === "/fleet" ? "active" : ""
                  }`}
                >
                  Fleet
                </Link>
              </div>

              <div className="dropdown-container">
                <Link to="/our-story" className="nav-link focus-ring dropdown-trigger">
                  About Us
                  <DropdownArrow />
                </Link>
                <div className="dropdown-menu">
                  <Link to="/our-story" className="dropdown-item">
                    About WayForSky
                  </Link>
                  <Link to="/why-wayforsky" className="dropdown-item">
                    Why WayForSky ?
                  </Link>
                </div>
              </div>

              <Link
                to="/contactus"
                className={`nav-link focus-ring ${location.pathname === "/contactus" ? "active" : ""}`}
              >
                Contact Us
              </Link>
            </nav>

            {/* Right Actions */}
            <div className="right-actions">
              <Link
                to="/login"
                className="contact-btn focus-ring"
                style={{ textDecoration: 'none' }}
              >
                Log in/Sign Up
              </Link>

              <button className="mobile-menu-btn focus-ring" aria-label="Menu" onClick={toggleMobileMenu}>
                <svg
                  id="hamburgerIcon"
                  width="20"
                  height="16"
                  viewBox="0 0 20 16"
                  fill="none"
                  style={{ display: isMobileMenuOpen ? "none" : "block" }}
                >
                  <path d="M0 1h20M0 8h20M0 15h20" stroke="#FFFFFF" strokeWidth="1.5" />
                </svg>
                <svg
                  id="closeIcon"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  style={{ display: isMobileMenuOpen ? "block" : "none" }}
                >
                  <path d="M15 5L5 15M5 5l10 10" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`mobile-menu ${isMobileMenuOpen ? "open" : ""}`}>
          <nav className="mobile-nav">
            <Link
              to="/"
              className={`mobile-nav-link focus-ring ${location.pathname === "/" ? "active" : ""}`}
              onClick={closeMobileMenu}
            >
              Home
            </Link>

            <div className="mobile-dropdown-container">
              <button
                className={`mobile-nav-link mobile-dropdown-trigger focus-ring ${
                  location.pathname.startsWith("/courses") || activeDropdown === "courses" ? "active" : ""
                }`}
              >
                <Link to="/courses" style={{ color: 'inherit', textDecoration: 'none' }} onClick={closeMobileMenu}>
                  Courses
                </Link>
                <MobileDropdownArrow onClick={() => toggleMobileDropdown("courses")} />
              </button>
              <div className={`mobile-dropdown-menu ${activeDropdown === "courses" ? "open" : ""}`}>
                <Link to="/zero-to-hero" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                  Zero to Hero Airline Ready Program - EASA
                </Link>
                <Link to="/commercial-pilot-license" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                  Commercial Pilot License with MEIR - SACAA
                </Link>
                <Link to="/private-pilot-license" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                  Private Pilot License
                </Link>
                <Link to="/flight-instructor-rating" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                  Flight Instructor Rating
                </Link>
                <Link to="/type-rating" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                  Type Rating A320 and B737 Cabin Crew
                </Link>
                <Link to="/cabin-crew" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                  Cabin Crew
                </Link>
                <Link to="/dgca-classes" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                  DGCA ground Classes
                </Link>
                <Link to="/elp-classes" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                  English Language Proficiency (ELP) Training
                </Link>
                <Link to="/nios-classes" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                  NIOS Prep Classes
                </Link>
              </div>
            </div>

            <Link
              to="/schools"
              className={`mobile-nav-link focus-ring ${location.pathname === "/schools" ? "active" : ""}`}
              onClick={closeMobileMenu}
            >
              Schools
            </Link>

            <div className="mobile-dropdown-container">
              <button
                className={`mobile-nav-link mobile-dropdown-trigger focus-ring ${
                  activeDropdown === "events" || location.pathname === "/fleet"
                    ? "active"
                    : ""
                }`}
                onClick={() => toggleMobileDropdown("events")}
              >
                Events
                <MobileDropdownArrow />
              </button>
              <div className={`mobile-dropdown-menu ${activeDropdown === "events" ? "open" : ""}`}>
                <Link
                  to="/fleet"
                  className="mobile-dropdown-item"
                  onClick={closeMobileMenu}
                >
                  Fleets
                </Link>

                <Link to="/testimonials" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                  Testimonials
                </Link>
              </div>
            </div>

            <div className="mobile-dropdown-container">
              <button
                className={`mobile-nav-link mobile-dropdown-trigger focus-ring ${activeDropdown === "aboutus" ? "active" : ""}`}
                onClick={() => toggleMobileDropdown("aboutus")}
              >
                About Us
                <MobileDropdownArrow />
              </button>
              <div className={`mobile-dropdown-menu ${activeDropdown === "aboutus" ? "open" : ""}`}>
                <Link to="/our-story" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                  About WayForSky
                </Link>
                <Link to="/why-wayforsky" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                  Why WayForSky ?
                </Link>
              </div>
            </div>

            <Link
              to="/contactus"
              className={`mobile-nav-link focus-ring ${location.pathname === "/contactus" ? "active" : ""}`}
              onClick={closeMobileMenu}
            >
              Contact Us
            </Link>
          </nav>
        </div>
      </header>
      <div style={{ height: "2px", backgroundColor: "white", width: "100%" }}></div>
    </>
  )
}